import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getPartnerTypeConfig, orderSections } from "@/lib/partnerTypeConfig";
import PartnerDiscover from "./PartnerDiscover";
import MilestoneCelebration from "@/components/MilestoneCelebration";
import { formatMinor, pickDisplay } from "@/lib/CurrencyFormatter";

/**
 * Iter XX.44.B — Business Dashboard v2 (partner-facing).
 * Shows: metrics, wallet snapshot, activity feed, achievements,
 * sharing assets, and cash-out entry. Every number is grounded in
 * real events — nothing fabricated.
 */
export default function PartnerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [activity, setActivity] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [coach, setCoach] = useState(null);
  const [studioEnabled, setStudioEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?return_to=/partner"); return; }
    let alive = true;
    async function load() {
      try {
        const [dRes, sRes, wRes, aRes, achRes, coachRes, studioRes] = await Promise.allSettled([
          api.get("/partners/me/dashboard/v2"),
          api.get("/partners/me/sharing"),
          api.get("/partners/me/wallet"),
          api.get("/partners/me/activity"),
          api.get("/partners/me/achievements"),
          api.get("/partners/me/growth-coach"),
          api.get("/partners/me/studio/status"),
        ]);
        if (!alive) return;
        if (dRes.status === "fulfilled") setDashboard(dRes.value.data);
        else if (dRes.reason?.response?.status === 404) { setError("no_profile"); setLoading(false); return; }
        else throw new Error("load_failed");
        if (sRes.status === "fulfilled") setSharing(sRes.value.data);
        if (wRes.status === "fulfilled") setWallet(wRes.value.data);
        if (aRes.status === "fulfilled") setActivity(aRes.value.data.activity || []);
        if (achRes.status === "fulfilled") setAchievements(achRes.value.data.achievements || []);
        if (coachRes.status === "fulfilled") setCoach(coachRes.value.data);
        if (studioRes.status === "fulfilled") setStudioEnabled(!!studioRes.value.data?.enabled);
        setLoading(false);
      } catch {
        if (alive) { setError("network"); setLoading(false); }
      }
    }
    load();
    return () => { alive = false; };
  }, [authLoading, user, navigate]);

  async function copy(text, label) {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy"); }
  }

  function downloadQR(dataUri, filename = "gully-qr.png") {
    try {
      const a = document.createElement("a"); a.href = dataUri; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Couldn't download QR"); }
  }

  if (authLoading || loading) return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading…</div>;
  if (error === "no_profile") {
    // Iter XX.44.L — non-partners visiting /partner see the full
    // Discover experience, not a tiny empty state.
    return <PartnerDiscover />;
  }
  if (error || !dashboard) {
    return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">{"Couldn't load your dashboard."}</div>;
  }

  // v2 payload shape: { window, lifetime, wallet, top_traffic_source, timeline }
  const win = dashboard.window || {};
  const life = dashboard.lifetime || {};
  const w = dashboard.wallet || {};
  const publicUrl = sharing?.public_url || `${window.location.origin}/@${user?.email || "you"}`;
  // Iter XX.48 — country-aware currency for wallet strip. Falls back
  // gracefully to canonical ledger currency when FX is unavailable.
  const disp = pickDisplay(wallet);
  const dispCcy = disp.currency;
  const dispAmounts = disp.amounts;
  const walletFmt = (minor) => formatMinor(minor ?? 0, { currency: dispCcy });
  // Fallback to major float (× 100) when the display block isn't ready
  // (e.g. wallet API failed but dashboard succeeded).
  const availableMinor = dispAmounts?.available ?? Math.round(Number(w.available ?? 0) * 100);
  const pendingMinor = dispAmounts?.pending ?? Math.round(Number(w.pending ?? 0) * 100);
  const paidLifetimeMinor = dispAmounts?.paid_lifetime ?? Math.round(Number(w.paid_lifetime ?? 0) * 100);
  const lifetimeGrossMinor = dispAmounts?.lifetime_gross ?? Math.round(Number(w.lifetime_gross ?? 0) * 100);
  // First-time state: never seen a visit or click yet.
  const isBrandNew = (win.visits ?? 0) === 0 && (win.clicks ?? 0) === 0
                     && (life.attributed_players ?? 0) === 0;
  // Iter XX.44.L — read partner_type + resolve personalization config.
  const partnerType = dashboard.partner_type || "other";
  const typeConfig = getPartnerTypeConfig(partnerType);

  // Section factories keyed by ID. `orderSections` reads the priority
  // list from typeConfig and returns the final render order — anything
  // not explicitly prioritised is appended in its stable default order.
  const DEFAULT_ORDER = [
    "first_steps", "wallet", "metrics", "achievements", "team_pulse_hint",
    "coach", "sharing", "studio", "activity", "earnings_placeholder",
  ];
  const renderOrder = orderSections(partnerType, DEFAULT_ORDER);

  function copyHint(hint) {
    if (hint === "copy_public") return () => copy(publicUrl, "Public page link");
    if (hint === "copy_referral" && sharing?.referral_url) return () => copy(sharing.referral_url, "Referral link");
    return null;
  }

  const sections = {
    first_steps: isBrandNew ? (
      <div className="bg-emerald-950/40 border border-emerald-800/60 rounded-xl p-5 mb-6" data-testid="partner-first-steps">
        <div className="text-xs uppercase tracking-widest text-emerald-400/80 mb-1" data-testid="partner-first-steps-persona">
          Welcome, {typeConfig.pronoun}
        </div>
        <div className="text-sm font-medium text-emerald-100 mb-3">Your page is live. Here&apos;s what to do next.</div>
        <ol className="space-y-2" data-testid="partner-first-steps-list">
          {typeConfig.checklist.map((item, i) => {
            const onCopy = copyHint(item.action_hint);
            const onPath = item.action_path ? () => navigate(item.action_path) : null;
            const onAct = onCopy || onPath;
            return (
              <li key={item.code} className="flex items-start gap-3" data-testid={`partner-first-steps-item-${item.code}`}>
                <span className={`inline-flex w-5 h-5 items-center justify-center rounded-full text-[10px] mt-0.5 shrink-0 ${item.done ? "bg-emerald-500 text-neutral-950" : "bg-neutral-800 text-neutral-300"}`}>
                  {item.done ? "✓" : i + 1}
                </span>
                <div className="flex-1">
                  <div className={`text-sm ${item.done ? "text-neutral-500 line-through" : "text-neutral-200"}`}>{item.label}</div>
                  {onAct && !item.done && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onAct}
                      className="border-emerald-700/60 text-emerald-200 mt-1.5"
                      data-testid={`partner-first-steps-cta-${item.code}`}
                    >
                      {item.action_cta}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-emerald-900/60">
          <Button size="sm" onClick={() => copy(publicUrl, "Public page link")} className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950" data-testid="partner-first-steps-copy">
            Copy my page link
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/partner/edit")} className="border-emerald-700/60 text-emerald-200" data-testid="partner-first-steps-edit">
            Add more to my page →
          </Button>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="partner-first-steps-view">
            <Button size="sm" variant="outline" className="border-neutral-700">View my public page →</Button>
          </a>
        </div>
      </div>
    ) : null,

    wallet: (
      <div className="bg-gradient-to-br from-emerald-950/60 to-neutral-900 border border-emerald-800/40 rounded-xl p-6 mb-6" data-testid="partner-wallet-strip">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-neutral-400 mb-1">Available balance</div>
            <div className="text-4xl font-semibold text-emerald-300" data-testid="wallet-available">{walletFmt(availableMinor)}</div>
            {disp.converted && (
              <div className="text-[10px] text-neutral-500 mt-1" data-testid="wallet-strip-fx-note">
                Converted from {disp.canonical} · rates {disp.fxStale ? "may be a day old" : "fresh"}
              </div>
            )}
          </div>
          <Button onClick={() => navigate("/partner/wallet")} className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950" data-testid="wallet-open-btn">
            Open Wallet →
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <WalletStat label="Pending" value={walletFmt(pendingMinor)} testid="wallet-pending" />
          <WalletStat label="Paid lifetime" value={walletFmt(paidLifetimeMinor)} testid="wallet-paid-lifetime" />
          <WalletStat label="Lifetime gross" value={walletFmt(lifetimeGrossMinor)} testid="wallet-lifetime-gross" />
        </div>
      </div>
    ),

    metrics: (
      <div key="metrics-block" data-testid="partner-metrics-block">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="partner-metrics-grid">
          <Metric label="Visits" value={win.visits ?? 0} testid="metric-visits" />
          <Metric label="Try-Gully clicks" value={win.clicks ?? 0} testid="metric-clicks" />
          <Metric label="New players" value={win.attributed_new ?? 0} testid="metric-new-players" />
          <Metric label="New Plus subs" value={win.plus_new ?? 0} testid="metric-new-plus" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <Metric label="Lifetime attributed" value={life.attributed_players ?? 0} testid="metric-lifetime-attributed" />
          <Metric label="Active Plus subscribers" value={life.plus_subscribers ?? 0} testid="metric-lifetime-plus" />
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4" data-testid="metric-top-source">
            <div className="text-2xl font-semibold text-emerald-300 capitalize">
              {dashboard.top_traffic_source || "—"}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Top traffic source</div>
          </div>
        </div>
        {isBrandNew && (
          <div className="text-xs text-neutral-500 -mt-4 mb-6" data-testid="partner-metrics-empty-hint">
            {typeConfig.empty.metrics}
          </div>
        )}
      </div>
    ),

    achievements: achievements.length > 0 ? (
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-6" data-testid="partner-achievements">
        <h2 className="text-sm text-neutral-400 mb-3">Milestones</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {achievements.map(a => (
            <div key={a.code} className="bg-neutral-950 border border-neutral-800 rounded-lg p-3" data-testid={`achievement-${a.code}`}>
              <div className="text-sm font-medium text-emerald-300">{a.title}</div>
              <div className="text-xs text-neutral-500 mt-1">{a.body}</div>
            </div>
          ))}
        </div>
      </div>
    ) : null,

    // Team Pulse hint — only shown for team-oriented partner types.
    team_pulse_hint: (["academy", "club", "school", "league"].includes(partnerType)) ? (
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-6" data-testid="partner-team-pulse-hint">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-neutral-100">Team Pulse</div>
            <div className="text-xs text-neutral-500 mt-1 leading-relaxed max-w-xl">
              Anonymised roster health that activates once you have 5+ registered players. Never exposes any individual conversation, memory, or report.
            </div>
          </div>
          <div className="text-[10px] text-neutral-600 shrink-0">K-anonymity ≥ 5</div>
        </div>
      </div>
    ) : null,

    coach: (coach && coach.nudges && coach.nudges.length > 0) ? (
      <div className="bg-neutral-900 border border-emerald-800/40 rounded-xl p-5 mb-6" data-testid="growth-coach-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm text-neutral-300 font-medium" data-testid="growth-coach-heading">{typeConfig.coach_hints.heading}</h2>
          <div className="text-[10px] text-neutral-500">Grounded in your own data</div>
        </div>
        <div className="space-y-2">
          {coach.nudges.slice(0, 3).map(n => (
            <div key={n.code} className="bg-neutral-950 border border-neutral-800 rounded-lg p-3" data-testid={`gc-nudge-${n.code}`}>
              <div className="text-sm font-medium text-emerald-200">{n.title}</div>
              <div className="text-xs text-neutral-400 mt-1 leading-relaxed">{n.body}</div>
              {n.action_cta && n.action_path && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(n.action_path)}
                  className="border-emerald-700/60 text-emerald-200 mt-2"
                  data-testid={`gc-cta-${n.code}`}
                >
                  {n.action_cta} →
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    ) : null,

    studio: studioEnabled ? (
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-6 flex items-center justify-between" data-testid="studio-quicklink">
        <div>
          <div className="text-sm font-medium">Marketing Studio</div>
          <div className="text-xs text-neutral-500 mt-1">Drafts for captions, reels, WhatsApp, email — you stay in control.</div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/partner/studio")}
          className="border-neutral-700 hover:bg-neutral-800"
          data-testid="studio-open-btn"
        >
          Open Studio →
        </Button>
      </div>
    ) : null,

    sharing: sharing ? (
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="partner-sharing-assets">
        <h2 className="text-base font-medium mb-4">One-click sharing</h2>
        <SharingBlock label="Referral link" text={sharing.referral_url} onCopy={() => copy(sharing.referral_url, "Referral link")} testid="sharing-referral-link" />
        <SharingBlock label="Instagram bio" text={sharing.instagram_bio_copy} onCopy={() => copy(sharing.instagram_bio_copy, "Instagram bio")} testid="sharing-instagram-bio" />
        <SharingBlock label="WhatsApp message" text={sharing.whatsapp_message} onCopy={() => copy(sharing.whatsapp_message, "WhatsApp message")} testid="sharing-whatsapp-message" />
        <SharingBlock label="Instagram Story copy" text={sharing.instagram_story_copy} onCopy={() => copy(sharing.instagram_story_copy, "Story copy")} testid="sharing-story-copy" />
        {sharing.qr_data_uri && (
          <div className="mt-6 flex items-center gap-4">
            <img src={sharing.qr_data_uri} alt="QR code" className="w-24 h-24 bg-white rounded" data-testid="sharing-qr-img" />
            <div>
              <div className="text-sm mb-2">Print this on posters or match sheets.</div>
              <Button variant="outline" size="sm" onClick={() => downloadQR(sharing.qr_data_uri, `gully-qr.png`)} className="border-neutral-700" data-testid="sharing-download-qr">
                Download QR
              </Button>
            </div>
          </div>
        )}
      </div>
    ) : null,

    activity: activity.length > 0 ? (
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="partner-activity-feed">
        <h2 className="text-base font-medium mb-4">Recent activity</h2>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {activity.map((a, i) => (
            <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b border-neutral-800 last:border-0" data-testid={`activity-row-${i}`}>
              <div>
                <div className="text-neutral-200">{a.title}</div>
                {a.platform && <div className="text-xs text-neutral-500 capitalize">via {a.platform}</div>}
              </div>
              <div className="text-xs text-neutral-500 whitespace-nowrap">{formatWhen(a.at)}</div>
            </div>
          ))}
        </div>
      </div>
    ) : (isBrandNew ? (
      <div className="bg-neutral-900 border border-dashed border-neutral-800 rounded-xl p-5 mb-6 text-center text-sm text-neutral-500" data-testid="partner-activity-empty">
        {typeConfig.empty.activity}
      </div>
    ) : null),

    earnings_placeholder: (!wallet) ? (
      <div className="bg-neutral-900 border border-dashed border-neutral-800 rounded-xl p-6 mb-8 text-center" data-testid="partner-earnings-placeholder">
        <div className="text-sm text-neutral-400">
          Partner earnings will appear here when the Gully Wallet launches.
        </div>
      </div>
    ) : null,
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="partner-dashboard" data-partner-type={partnerType}>
      <MilestoneCelebration achievements={achievements} />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8 gap-3 flex-wrap">
          <div>
            <Link to="/me" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="partner-dashboard-back">← Back to account</Link>
            <div className="flex items-center gap-2 mt-2">
              <h1 className="text-3xl font-semibold">Your Earn Hub</h1>
              <span className="text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-950/60 border border-emerald-800/60 rounded-full px-2 py-0.5" data-testid="partner-type-badge">{typeConfig.label}</span>
            </div>
            <div className="text-neutral-400 mt-1" data-testid="partner-header-username">Last 30 days</div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 min-w-0">
            <div className="text-xs text-neutral-500">Public page</div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 text-sm break-all" data-testid="partner-public-url">{publicUrl}</a>
              <Button variant="outline" size="sm" onClick={() => copy(publicUrl, "Public page link")} className="border-neutral-700" data-testid="partner-copy-public-url">Copy</Button>
              {sharing?.referral_url && (
                <Button variant="outline" size="sm" onClick={() => copy(sharing.referral_url, "Referral link")} className="border-emerald-800/60 text-emerald-200" data-testid="partner-copy-referral-url">Copy referral link</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate("/partner/edit")} className="border-neutral-700" data-testid="partner-edit-page-btn">Edit page</Button>
            </div>
          </div>
        </div>

        {renderOrder.map(id => sections[id] ? <React.Fragment key={id}>{sections[id]}</React.Fragment> : null)}

        <div className="text-xs text-neutral-500 mt-8">
          {dashboard.grounded_note || "All numbers are derived from real events. Gully never shares private player conversations, memories or reports with partners."}
        </div>
      </div>
    </div>
  );
}

function WalletStat({ label, value, testid }) {
  return (
    <div className="bg-neutral-950/40 border border-neutral-800 rounded p-2" data-testid={testid}>
      <div className="text-neutral-500">{label}</div>
      <div className="text-neutral-100 text-sm mt-0.5">{value}</div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMin = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function Metric({ label, value, testid }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4" data-testid={testid}>
      <div className="text-2xl font-semibold text-emerald-300">{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function SharingBlock({ label, text, onCopy, testid }) {
  return (
    <div className="mb-4 last:mb-0" data-testid={testid}>
      <div className="text-xs text-neutral-500 mb-1">{label}</div>
      <div className="flex items-start gap-2">
        <div className="flex-1 text-sm text-neutral-200 bg-neutral-950 border border-neutral-800 rounded p-3 whitespace-pre-wrap break-words">{text}</div>
        <Button variant="outline" size="sm" onClick={onCopy} className="border-neutral-700 shrink-0" data-testid={`${testid}-copy`}>Copy</Button>
      </div>
    </div>
  );
}
